import * as React from 'react'
import { useNavigate} from 'react-router-dom';
import { atom, useRecoilState} from "recoil";
import { useAccentDisplay } from '../controls/AccentDisplay';
import { clearEntities, getEntityMemberValue, saveEntities } from '../services/DataService';
import { accentUtils, compareObjects, formHelper, from, showOK, t, cacheAdd } from '../services/HelperService';
import { ViewWithToolbars } from './ViewToolbar';



export function getDefaultViewState(isReadOnly, isNew) {
    return {
        isReadOnly: isReadOnly??false,
        isDirty: isNew??false,
        isNew: isNew??false,
        isValid: true
    };
}

export const activeViewState = atom({
    key: `activeViewState`,
    default: null
});


export const validationResults = {
    default: "Default",
    save: "Save",
    cancel: "Cancel",
    discard: "Discard"    
}



export function useViewState() {

    const [viewState, setViewState] = useRecoilState(activeViewState);



    const update = React.useCallback(e => {

        const newState = { ...viewState, ...e };

        if (compareObjects(viewState, newState)) return;

        setViewState(newState);

    }, [viewState, setViewState]);


    return {
        viewState: viewState,
        update: update
    };
}


export const withViewState = (Component) => React.memo(React.forwardRef((props, ref) => {

    const info = useViewState();

    return <Component {...props} ref={ref} viewState={info.viewState} setViewState={info.update}/>;

}));

function getDefaultReadOnly(props) {
    return accentUtils.isNull(props.view.defaultReadOnly) ? false : props.view.defaultReadOnly(props.viewData)
}

function defaultAutoSaveFunction() {
    return Promise.resolve({ allow: true});
}

window.accentAutoSave = defaultAutoSaveFunction;

export const withToolbar = (Component, additionalProps ) => React.memo((props) => {

    const { hideSave, hideUndo } = additionalProps ?? {};

    const [viewComponentRefreshKey, setViewComponentRefreshKey] = React.useState(1);
    // Whatever the view's afterSave() resolved with on the save that caused the current
    // mount of the view component. A save normally remounts the view (refreshOnSaved),
    // which discards its state; this prop lets the fresh mount pick up the outcome of
    // post-save work (e.g. a failed reprice). A new entity redirects instead of refreshing,
    // so the same value travels through the AdditionalViewData cache for that path.
    const [lastSaveResult, setLastSaveResult] = React.useState(() => props.additionalViewData?.lastSaveResult ?? null);
    const toolbarView = React.useRef();
    const viewRef = React.useRef();
    const [viewState, setViewState] = useRecoilState(activeViewState);    
    const display = useAccentDisplay();
    const navigate = useNavigate();
    const initialStateReady = React.useRef(false);
    
    const refreshToolbar = React.useCallback(() => {

        if (toolbarView.current) {
            toolbarView.current.refreshToolbar();
        }

    }, [toolbarView])

    const beforeValidateView = () => {
        if (!accentUtils.isNull(viewRef.current) && !accentUtils.isNull(viewRef.current.beforeValidate))
            return viewRef.current.beforeValidate();
        return Promise.resolve();
    };

    const beforeSave = () => {
        if (!accentUtils.isNull(viewRef.current) && !accentUtils.isNull(viewRef.current.beforeSave)) {
            return viewRef.current.beforeSave();
        }

        return Promise.resolve();
    };

    const afterSave = () => {

        if (!accentUtils.isNull(viewRef.current) && !accentUtils.isNull(viewRef.current.afterSave))
            return viewRef.current.afterSave();
        else {
            return Promise.resolve();
        }
    };

    const setAsDiscarded = (e) => {
        clearEntities();

        if (e.sameView) {
            props.refresh();
        }
    };

    const onRefresh = React.useCallback(e => {

        if (props.refresh) {
            props.refresh(e);
        }

    },[props]);


    

    const saveData = () => {

        return new Promise((resolve, reject) => {

            saveEntities().then(function () {
                resolve();
            }).catch(function (err) {

                var msg = "";
                var custom = null;

                if (accentUtils.isNull(err)) {
                    msg = "Unknown error occurred saving data. please try again";
                } else {

                    if (err.entityErrors) {
                        if (err.entityErrors.length > 0) {
                            msg = err.entityErrors[0].errorMessage;
                            custom = err.entityErrors[0].custom;
                        }
                    } else {
                        if (err.message)
                            msg = err.message;
                        else
                            msg = err;
                    }
                }

                showOK(
                    "application_strings.application.dialogs.viewValidationTitle",
                    custom ? t(msg, custom) : t(msg),
                    ["application_strings.application.buttons.close"],
                    false,
                    null,
                    true
                );
                reject(err);
            });
        });
    }

    const validateView = () => {
        return new Promise((resolve, reject) => {

            beforeValidateView().then(function (result) {

                if (!formHelper.isViewValid() || !accentUtils.isEmpty(result)) {
                    if (result === validationResults.cancel || result === validationResults.discard || result === validationResults.save) {
                        resolve(result);
                    } else {


                        showOK("application_strings.application.dialogs.viewValidationTitle", "application_strings.application.dialogs.viewValidationMsg", ["application_strings.application.buttons.discardChanges", "application_strings.application.buttons.editChanges"], false, null, true, 800).then(function (res) {
                            if (res === "application_strings.application.buttons.discardChanges") {
                                resolve(validationResults.discard);
                            } else {
                                formHelper.showInvalidFields();
                                resolve(validationResults.cancel);
                            }
                        });
                    }
                } else {
                    resolve(validationResults.save);
                }

            }).catch((e) => {
                reject(e)
            });

        });
    };




    const saveViewInternal = React.useCallback((e) => {

        const wasNew = viewState.isNew;

        return new Promise((resolve, reject) => {

            validateView().then(function (res) {

                if (res === validationResults.cancel) {
                    resolve({ saved: false });

                } else if (res === validationResults.save) {
                    beforeSave().then(function (beforeSaveArgs) {
                        saveData().then(function () {


                            let redirect = null;
                            let defaultPrompt = null;

                            if (wasNew) {

                                const entityID = accentUtils.isNull(beforeSaveArgs?.redirectID) ? (!accentUtils.isNull(props.view?.newEntityPath) ? getEntityMemberValue(props.viewData, `${props.view.newEntityPath}.ID`) : props.viewData?.ID) : beforeSaveArgs.redirectID;

                                if (!accentUtils.isNull(entityID)) {
                                    redirect = props.path.replace(":id", entityID);
                                }


                                defaultPrompt = (!accentUtils.isNull(props.view?.prompt)) ? props.view.prompt(props.viewData) : null;

                                if (beforeSaveArgs?.processRedirect) {
                                    redirect = beforeSaveArgs.processRedirect({
                                        path: props.path,
                                        entityID: entityID,
                                        redirect: redirect,
                                        defaultPrompt: defaultPrompt
                                    });
                                }

                            }

                            setViewState(getDefaultViewState(getDefaultReadOnly(props), false));

                            return afterSave().then(afterSaveResult => {
                                resolve({ saved: true, redirect: redirect, from: `${window.location.pathname}${window.location.search}`, defaultPrompt: defaultPrompt, afterSaveResult: afterSaveResult ?? null });
                            });

                        }).catch(function (e) {
                            console.error(e);
                            reject({ saved: false });
                        });
                    }).catch(function (e) {
                        console.error(e);
                        reject({ saved: false });
                    });

                } else {

                    resolve({ saved: false, discarded: true });
                    setAsDiscarded({ sameView: !e.navigating });
                }

            }).catch(function (e) {
                console.error(e);
                reject({ saved: false });

            });
        });

    }, [setViewState, viewState, props._defaultReadOnly]);

    const saveView = React.useCallback((e) => {

        return new Promise(p => {

            saveViewInternal(e).then(res => {

                if (!accentUtils.isEmpty(res.redirect)) {

                    window.onOneTimeAccentRouted[res.redirect] = () => {
                        p(res);
                    };
                    // The redirected view is a fresh route mount; AdditionalViewData is the
                    // only channel into it, so the afterSave outcome rides along with it.
                    const additionalViewData = accentUtils.isNull(res.afterSaveResult)
                        ? e?.additionalViewDataOnSaved
                        : { ...(e?.additionalViewDataOnSaved ?? {}), lastSaveResult: res.afterSaveResult };
                    if (!accentUtils.isNull(additionalViewData)) {
                        cacheAdd(additionalViewData, "AdditionalViewData");
                    }
                    navigate(res.redirect, { replace: true, state: { defaultPrompt: res.defaultPrompt } });

                } else {

                    if (res.saved) {

                        if (!props.view.preventRefreshAfterSave) {
                            if (e?.refreshOnSaved) {
                                if (!accentUtils.isNull(e?.additionalViewDataOnSaved)) {
                                    cacheAdd(e.additionalViewDataOnSaved, "AdditionalViewData");
                                }
                                // Same React batch as the key bump, so the remounted view
                                // sees the result of this save on its first render.
                                setLastSaveResult(res.afterSaveResult ?? null);
                                setViewComponentRefreshKey(x => x + 1);
                            }
                        }
                    }
                    p(res);
                }
                
                

                

            }).catch(res => {
                p(res);
            });
        });


    }, [saveViewInternal, setViewComponentRefreshKey, props]);

    const autoSave = React.useCallback((e) => {

        const requireSave = viewState.isDirty;

        return new Promise(p => {

            if (!requireSave) {
                p({ allow: true });
                return;
            }

            const isNew = props.params.id === "NEW";

            saveView({ navigating: true, navigatingBack: e?.navigatingBack }).then(r => {
                p({
                    allow: r.saved || r.discarded,
                    replace: r.discarded && isNew,
                    isNew: isNew
                });
            });


        });


    }, [saveView, viewState]);

    const update = React.useCallback((e, force) => {

        const hasChanged = from(Object.keys(e)).any(k => e[k] !== viewState[k]);

        if (!hasChanged && !force) return;

        setViewState(s => { return { ...s, ...e }; });

    }, [viewState, setViewState]);


    React.useEffect(() => {

        if (accentUtils.isNull(viewState)) return;

        if (viewState.isValid != formHelper.isViewValid()) {
            setViewState(v => { return { ...v, isValid: formHelper.isViewValid() } });
        }

    },[viewState, navigate]);

    React.useEffect(() => {

        const isNew = props.params.id === "NEW";

        if (isNew) {
            setViewState(s => { return { ...s, isNew: true, isDirty: true }; });
        }

    },[]);

    React.useEffect(() => {

        const isNew = props.params.id === "NEW";

        setViewState(getDefaultViewState(getDefaultReadOnly(props), isNew));

        initialStateReady.current = true;

        return () => {
            window.accentAutoSave = defaultAutoSaveFunction;

            setViewState(null);
        };

    }, []); 



    window.accentAutoSave = autoSave;

    const isViewStateReady = initialStateReady.current && !accentUtils.isNull(viewState) && (props.params.id === "NEW" && viewState.isNew || props.params.id !== "NEW");

    
    

    const entityDetailsQuery = (accentUtils.isNull(props.view?.entityDetails) || accentUtils.isNull(props.viewData))
        ? null
        : props.view.entityDetails(props.viewData);

    return isViewStateReady ? <ViewWithToolbars
        ref={toolbarView}
        refresh={props.refresh}
        navigate={navigate}
        display={display}
        viewState={viewState}
        saveView={saveView}
        view={<Component key={viewComponentRefreshKey} ref={viewRef} {...props} refresh={onRefresh} viewState={viewState} setViewState={update} saveView={saveView} refreshToolbar={refreshToolbar} lastSaveResult={lastSaveResult} />}
        viewRef={viewRef}
        hideSave={hideSave}
        hideUndo={hideUndo}
        auditHistoryQuery={props.view.auditLog}
        entityDetailsQuery={entityDetailsQuery}
    />: null;      

});



